import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Brain, Loader2, CheckCircle2, AlertTriangle, Save } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIModelSettings() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/ai/config`),
        axios.get(`${API}/ai/status`),
      ]);
      setConfig(c.data);
      setStatus(s.data);
      setSelected(c.data.model);
    } catch (e) {
      toast.error("Failed to load AI settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ai/model`, { model: selected });
      toast.success("AI model updated — it now applies across every AI feature");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update model");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-[#8A857A] py-10" data-testid="ai-settings-loading"><Loader2 className="w-5 h-5 animate-spin" /> Loading AI settings…</div>;
  }

  const dirty = selected !== config?.model;

  return (
    <div className="space-y-5" data-testid="ai-model-settings">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
          <Brain className="w-5 h-5 text-[#1D4ED8]" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-[#1A1A1A]">AI Model</h3>
          <p className="text-sm text-[#59554D]">Choose the model that powers every AI feature — meeting notes, Competitor Spy, AI Visibility and analytics</p>
        </div>
      </div>

      {/* Health strip */}
      <div className={`rounded-lg border p-3 flex items-center gap-2 text-sm ${status?.healthy ? "border-[#E3F3E9] bg-[#E3F3E9] text-[#0E6B3F]" : "border-[#FFE9E0] bg-[#FFE9E0] text-[#C23A00]"}`} data-testid="ai-settings-health">
        {status?.healthy ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {status?.healthy
          ? <span>Connected to OpenAI — active model <b>{status?.model}</b> is healthy</span>
          : <span>AI unavailable{status?.detail ? `: ${status.detail}` : ""}</span>}
      </div>

      {/* Model options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {config?.options?.map((o) => {
          const active = selected === o.id;
          return (
            <button
              key={o.id}
              onClick={() => setSelected(o.id)}
              className={`text-left rounded-xl border p-4 transition-colors ${active ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-[#DAD5CA] bg-[#FFFFFF] hover:bg-[#FFFFFF]"}`}
              data-testid={`ai-model-option-${o.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[#1A1A1A] font-medium">{o.label}</span>
                <div className="flex items-center gap-1.5">
                  {o.recommended && <Badge className="text-[10px] border-0 bg-[#E3F3E9] text-[#0E6B3F]">Recommended</Badge>}
                  {active && <CheckCircle2 className="w-4 h-4 text-[#1D4ED8]" />}
                </div>
              </div>
              <div className="text-[11px] uppercase tracking-wider text-[#8A857A] mb-1">{o.tier}</div>
              <p className="text-xs text-[#59554D]">{o.description}</p>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={!dirty || saving} className="bg-[#1D4ED8] hover:bg-[#EFF6FF] text-[#1A1A1A] font-medium" data-testid="ai-model-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save model
        </Button>
        {dirty && <span className="text-xs text-[#6B5600]">Unsaved change — currently active: {config?.model}</span>}
      </div>
    </div>
  );
}
